<template>
    <div class="custom-header" :class="{ 'dark-mode': isDarkMode, 'menu-open': menuOpen }">
        <div class="hero-background">
            <div class="gradient-orb orb-1"></div>
            <div class="gradient-orb orb-2"></div>
            <div class="gradient-orb orb-3"></div>
        </div>
        <div class="header-content">
            <div class="header-left">
                <button v-if="isConfigured" @click="openSideMenu" class="app-logo" title="Menu">
                    <i class="fa-solid fa-wallet"></i>
                </button>
                <div v-else class="app-logo">
                    <i class="fa-solid fa-wallet"></i>
                </div>
            </div>

            <div class="header-center">
                <!-- Draggable area -->
            </div>

            <div class="header-right">
                <!-- User Info (when authenticated) -->
                <!-- <div v-if="user" class="user-info">
                    <span class="user-email">{{ userEmail }}</span>
                    <div class="user-avatar">
                        <i class="fa-solid fa-user"></i>
                    </div>
                </div> -->

                <div v-if="isConfigured && isViewingCurrentMonth" class="remaining-preview header-btn">
                    <span class="remaining-amount">
                        ${{ formatCurrency(remainingToSpend) }} </span>
                    <span class="remaining-label">left</span>
                </div>

                <button v-if="isConfigured" @click="$emit('edit-budget')" class="header-btn"
                    :disabled="!isViewingCurrentMonth"
                    :title="isViewingCurrentMonth ? 'Edit Monthly Budget' : 'Cannot edit budget for past months'">
                    <i class="bi bi-pencil"></i>
                </button>

                <button v-if="user && isConfigured" @click="openAssistantModal" class="header-btn d-md-none" title="Financial Assistant">
                    <i class="fa-solid fa-wand-magic-sparkles"></i>
                </button>

                <button v-if="user && isConfigured" @click="openThemeModal" class="header-btn" title="Change Theme">
                    <i class="fa-solid fa-palette"></i>
                </button>

                <!-- Sign Out Button (when authenticated) -->
                <button v-if="user" @click="handleSignOut" class="header-btn signout-btn" title="Sign Out">
                    <i class="fa-solid fa-sign-out-alt"></i>
                </button>
            </div>
        </div>

        <!-- Mobile Category Navigation Row -->
        <div v-if="isConfigured && categoryOrder.length > 0" class="mobile-category-row d-md-none">
            <div class="category-indicator">
                <div class="category-dots">
                    <span v-for="category in categoryOrder" :key="category" class="dot"
                        :class="{ active: selectedCategory === category }" @click="updateCategory(category)">
                    </span>
                </div>
                <div class="category-label ps-1 pe-1">
                    <i class="fa-solid" :class="{
                        'fa-th-large': selectedCategory === 'all',
                        'fa-wallet': selectedCategory === 'budget',
                        'fa-chart-line': selectedCategory === 'spending',
                        'fa-piggy-bank': selectedCategory === 'savings',
                        'fa-tags': selectedCategory === 'categories'
                    }"></i>
                    {{ categoryLabels[selectedCategory] }}
                </div>
            </div>
        </div>

        <!-- Quick Transaction Modal -->
        <Teleport to="body">
            <div v-if="showQuickTransactionModal" class="modal-overlay" @click="closeQuickTransaction"
                @touchmove.prevent>
                <div class="modal-content" @click.stop @touchmove.stop>
                    <div class="modal-header">
                        <h4 class="modal-title">
                            <i class="fa-solid fa-plus me-2"></i>Quick Transaction
                        </h4>
                        <button @click="closeQuickTransaction" class="btn-close">
                            <i class="fa-solid fa-times"></i>
                        </button>
                    </div>
                    <div class="modal-body">
                        <NaturalLanguageTransaction @processing-started="handleProcessingStarted"
                            @transaction-processed="handleTransactionProcessed"
                            @transactions-confirmed="handleTransactionsConfirmed" />
                    </div>
                </div>
            </div>
        </Teleport>

        <!-- Theme Picker Modal -->
        <Teleport to="body">
            <div v-if="showThemeModal" class="modal-overlay" @click="closeThemeModal" @touchmove.prevent>
                <div class="modal-content theme-modal" @click.stop @touchmove.stop>
                    <div class="modal-header">
                        <h4 class="modal-title">
                            <i class="fa-solid fa-palette me-2"></i>Choose Theme
                        </h4>
                        <button @click="closeThemeModal" class="btn-close">
                            <i class="fa-solid fa-times"></i>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="theme-grid">
                            <div v-for="theme in themes" :key="theme.id" class="theme-option"
                                :class="{ active: currentTheme === theme.id }" @click="selectTheme(theme.id)">
                                <div class="theme-preview">
                                    <div class="preview-colors">
                                        <div class="color-bar" :style="{ background: theme.colors.background }"></div>
                                        <div class="color-bar" :style="{ background: theme.colors.card }"></div>
                                        <div class="color-bar" :style="{ background: theme.colors.primary }"></div>
                                        <div class="color-bar" :style="{ background: theme.colors.border }"></div>
                                    </div>
                                    <div class="theme-icon">
                                        <i class="fa-solid" :class="theme.icon"></i>
                                    </div>
                                </div>
                                <div class="theme-info">
                                    <div class="theme-name">{{ theme.name }}</div>
                                    <div v-if="currentTheme === theme.id" class="theme-active-badge">
                                        <i class="fa-solid fa-check"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>

        <!-- Financial Assistant Modal -->
        <Teleport to="body">
            <div v-if="showAssistantModal" class="modal-overlay assistant-modal-overlay" @click="closeAssistantModal" @touchmove.prevent>
                <div class="modal-content assistant-modal-content" @click.stop>
                    <button @click="closeAssistantModal" class="assistant-close-btn">
                        <i class="fa-solid fa-times"></i>
                    </button>
                    <div class="assistant-modal-body">
                        <FinancialAssistantChat />
                    </div>
                </div>
            </div>
        </Teleport>


    </div>
</template>

<script>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useDarkMode } from '../composables/useDarkMode';
import { useAuth } from '../composables/useAuth';
import { useBudgetData } from '../composables/useBudgetData';
import { useRemainingBudget } from '../composables/useRemainingBudget';
import NaturalLanguageTransaction from './NaturalLanguageTransaction.vue';
import FinancialAssistantChat from './FinancialAssistantChat.vue';

export default {
    name: 'CustomHeader',
    components: {
        NaturalLanguageTransaction,
        FinancialAssistantChat
    },
    props: {
        isConfigured: {
            type: Boolean,
            default: false
        },
        selectedCategory: {
            type: String,
            default: 'all'
        },
        categoryLabels: {
            type: Object,
            default: () => ({})
        },
        categoryOrder: {
            type: Array,
            default: () => []
        },
        menuOpen: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const { currentTheme, isDarkMode, setTheme, themes } = useDarkMode();
        const { user, signOut } = useAuth();
        const { dailyRemainingBudget, savingsGoal, isViewingCurrentMonth } = useBudgetData();
        const { adjustedRemainingBudget, formatCurrency } = useRemainingBudget();
        const showQuickTransactionModal = ref(false);
        const showThemeModal = ref(false);
        const showAssistantModal = ref(false);

        // iOS Safari glass effect fix - minimal approach
        const fixIOSViewport = () => {
            if (typeof window !== 'undefined') {
                // Set fixed height to prevent iOS Safari viewport changes
                document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);

                // Safari glass effect fix: Don't use overflow: hidden, use touch-action instead
                // This prevents WebKit from disabling backdrop-filter while keeping scroll position
                document.body.style.touchAction = 'none';
                document.body.style.userSelect = 'none';

                // Add class for additional CSS targeting if needed
                document.body.classList.add('modal-open-safari-fix');
            }
        };

        const restoreIOSViewport = () => {
            if (typeof window !== 'undefined') {
                // Remove modal state class
                document.body.classList.remove('modal-open-safari-fix');

                // Restore touch and selection behavior
                document.body.style.touchAction = '';
                document.body.style.userSelect = '';

                // Remove custom viewport height
                document.documentElement.style.removeProperty('--vh');
            }
        };

        // Watch for modal show/hide to handle iOS fixes
        watch(showQuickTransactionModal, (newValue) => {
            if (newValue) {
                fixIOSViewport();
            } else {
                restoreIOSViewport();
            }
        });

        // Cleanup on unmount
        onUnmounted(() => {
            if (showQuickTransactionModal.value) {
                restoreIOSViewport();
            }
        });

        const userEmail = computed(() => {
            if (!user.value) return '';
            return user.value.attributes?.email || user.value.username || 'User';
        });

        const handleSignOut = async () => {
            try {
                await signOut();
                emit('signed-out');
            } catch (error) {
                console.error('Sign out error:', error);
            }
        };

        const openQuickTransaction = () => {
            showQuickTransactionModal.value = true;
        };

        const closeQuickTransaction = () => {
            showQuickTransactionModal.value = false;
        };

        const openThemeModal = () => {
            showThemeModal.value = true;
        };

        const closeThemeModal = () => {
            showThemeModal.value = false;
        };

        const selectTheme = (themeId) => {
            setTheme(themeId);
            closeThemeModal();
        };

        let savedScrollY = 0;

        const lockBody = () => {
            savedScrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${savedScrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.overflow = 'hidden';
        };

        const unlockBody = () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.overflow = '';
            window.scrollTo(0, savedScrollY);
        };

        const openAssistantModal = () => {
            showAssistantModal.value = true;
            lockBody();
        };

        const closeAssistantModal = () => {
            showAssistantModal.value = false;
            unlockBody();
        };

        const openSideMenu = () => {
            emit('menu-opened');
        };

        const handleProcessingStarted = (data) => {
            // Pass the event up to the parent component
            emit('processing-started', data);
        };

        const handleTransactionProcessed = (data) => {
            // Pass the event up to the parent component
            emit('transaction-processed', data);
        };

        const handleTransactionsConfirmed = (data) => {
            // Pass the event up to the parent component and close modal
            emit('transactions-confirmed', data);
            closeQuickTransaction();
        };

        const remainingToSpend = computed(() => {
            return adjustedRemainingBudget.value;
        });

        const getCategoryCardCount = (categoryKey) => {
            if (categoryKey === 'all') {
                return 13;
            }

            const allCards = [
                { component: 'TodayRemaining', category: 'budget' },
                { component: 'CategorySpendingLimits', category: 'categories' },
                { component: 'CategoryExpensesChart', category: 'categories' },
                { component: 'CategoryKeywords', category: 'categories' },
                { component: 'AverageDailySaved', category: 'savings' },
                { component: 'AverageDailySpending', category: 'spending' },
                { component: 'RunningSavings', category: 'savings' },
                { component: 'DailySavingsGoal', category: 'savings' },
                { component: 'SavingsGoals', category: 'savings' },
                { component: 'MonthlyExpensesChart', category: 'budget' },
                { component: 'MonthlyExpensesBreakdown', category: 'budget' },
                { component: 'MonthlySavingsChart', category: 'savings' },
                { component: 'DailyExpensesChart', category: 'spending' }
            ];

            return allCards.filter(card => card.category === categoryKey).length;
        };

        const updateCategory = (category) => {
            emit('update:selectedCategory', category);
        };

        return {
            currentTheme,
            isDarkMode,
            themes,
            user,
            userEmail,
            handleSignOut,
            showQuickTransactionModal,
            openQuickTransaction,
            closeQuickTransaction,
            showThemeModal,
            openThemeModal,
            closeThemeModal,
            selectTheme,
            showAssistantModal,
            openAssistantModal,
            closeAssistantModal,
            openSideMenu,
            handleProcessingStarted,
            handleTransactionProcessed,
            handleTransactionsConfirmed,
            remainingToSpend,
            formatCurrency,
            getCategoryCardCount,
            updateCategory,
            isViewingCurrentMonth
        };
    },
    emits: ['edit-budget', 'reset-app', 'signed-out', 'transaction-processed', 'transactions-confirmed', 'update:selectedCategory', 'menu-opened', 'processing-started']
}
</script>

<style scoped>
.custom-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: var(--header-background);
    z-index: 1000;
    -webkit-app-region: drag;
    user-select: none;
    transition: all 0.3s ease;
    overflow: hidden;
}

/* Desktop: shrink header when menu opens */
@media (min-width: 769px) {
    .custom-header.menu-open {
        left: 225px;
        width: calc(100% - 225px);
    }
}

/* Mobile: no shrink, overlay mode */
@media (max-width: 768px) {
    .custom-header.menu-open {
        left: 0;
        width: 100%;
    }
}

.hero-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    z-index: 0;
}

.gradient-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.5;
}

.orb-1 {
    width: 400px;
    height: 400px;
    background: linear-gradient(135deg, var(--orb-1-start), var(--orb-1-end));
    top: -200px;
    left: -100px;
    animation: float1 20s ease-in-out infinite;
}

.orb-2 {
    width: 350px;
    height: 350px;
    background: linear-gradient(135deg, var(--orb-2-start), var(--orb-2-end));
    top: -100px;
    right: -100px;
    animation: float2 18s ease-in-out infinite;
}

.orb-3 {
    width: 300px;
    height: 300px;
    background: linear-gradient(135deg, var(--orb-3-start), var(--orb-3-end));
    bottom: -150px;
    left: 50%;
    animation: float3 22s ease-in-out infinite;
}

@keyframes float1 {
    0%, 100% {
        transform: translate(0, 0) scale(1);
    }
    33% {
        transform: translate(120px, -60px) scale(1.2);
    }
    66% {
        transform: translate(-80px, 80px) scale(0.85);
    }
}

@keyframes float2 {
    0%, 100% {
        transform: translate(0, 0) scale(1);
    }
    33% {
        transform: translate(-100px, 100px) scale(1.15);
    }
    66% {
        transform: translate(80px, -60px) scale(0.9);
    }
}

@keyframes float3 {
    0%, 100% {
        transform: translateX(-50%) translate(0, 0) scale(1);
    }
    33% {
        transform: translateX(-50%) translate(60px, -80px) scale(1.18);
    }
    66% {
        transform: translateX(-50%) translate(-120px, 60px) scale(0.88);
    }
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 0 20px;
    max-width: 100%;
    position: relative;
    z-index: 1;
}

.header-left {
    display: flex;
    align-items: center;
    -webkit-app-region: no-drag;
}

.app-logo {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, var(--btn-primary-color), color-mix(in srgb, var(--btn-primary-color) 80%, black));
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.app-logo:hover {
    transform: scale(1.05);
}

.app-logo:active {
    transform: scale(0.95);
}

.remaining-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    min-height: 48px;
    padding: 0.5rem 0.75rem;
    border: none;
    border-radius: 10px;
    background: var(--light-bg);
    transition: all 0.2s ease;
}

.remaining-preview:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
}

.remaining-amount {
    font-size: 15px;
    font-weight: 700;
    color: var(--light-text);
    line-height: 1.2;
}

.remaining-amount.negative {
    color: var(--light-danger);
}

.remaining-label {
    font-size: 10px;
    color: var(--light-text);
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
    margin-top: 2px;
}

.app-logo i {
    font-size: 1.5rem;
    color: white;
}

.app-title {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    letter-spacing: -0.5px;
}

.header-center {
    flex: 1;
    height: 100%;
    -webkit-app-region: drag;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    -webkit-app-region: no-drag;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0.5rem 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    transition: all 0.2s ease;
}

.user-email {
    font-size: 0.85rem;
    color: var(--light-text);
    font-weight: 500;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-avatar {
    width: 32px;
    height: 32px;
    background: var(--light-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.9rem;
}

.signout-btn:hover {
    background: rgba(255, 193, 7, 0.8);
}

.app-logo i {
    color: white;
}

.header-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    min-height: 48px;
    padding: 0.75rem;
    border: none;
    border-radius: 10px;
    background: var(--light-bg);
    color: var(--light-text);
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1.125rem;
}

.header-btn i {
    font-size: 1.25rem;
}

.header-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
}

.header-btn:active {
    transform: translateY(0);
    transform: scale(0.95);
}

.header-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.header-btn:disabled:hover {
    background: var(--light-bg);
    transform: none;
}

.reset-btn:hover {
    background: rgba(220, 53, 69, 0.8);
}

@media (min-width: 768px) {
    .custom-header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 80px;
        background: rgba(var(--light-bg-rgb), 0.65);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        z-index: 1000;
        -webkit-app-region: drag;
        user-select: none;
        transition: all 0.3s ease;
        overflow: hidden;
    }

    .header-btn {
        background: var(--bg-card);
        height: 59px;
        min-width: 59px;
    }
}

/* Mobile Category Navigation Row */
.mobile-category-row {
    background: var(--header-background);
    padding: 0.75rem 1rem 1.2rem 1rem;
    position: relative;
    z-index: 1;
}

.category-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
}

.category-dots {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.category-dots .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
    cursor: pointer;
}

.category-dots .dot.active {
    width: 24px;
    border-radius: 4px;
    background: var(--light-text);
}

.category-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: var(--light-text);
    font-size: 0.95rem;
}

.category-label i {
    color: var(--light-text);
    font-size: 1.1rem;
}

.card-count-badge {
    background: var(--light-bg);
    color: var(--light-text);
    padding: 0.125rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    margin-left: 0.25rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .custom-header {
        height: auto;
    }

    .gradient-orb {
        display: none;
    }

    .header-content {
        padding: 10px;
    }

    .app-title {
        display: none;
    }

    .remaining-preview {
        min-width: 48px;
        min-height: 48px;
        padding: 0.4rem 0.625rem;
    }

    .remaining-amount {
        font-size: 13px;
    }

    .remaining-label {
        font-size: 9px;
    }

    .header-btn {
        min-width: 52px;
        min-height: 52px;
        padding: 0.625rem;
    }

    .header-btn i {
        font-size: 1.15rem;
    }

    .color-swatch {
        width: 22px;
        height: 22px;
    }

    .color-indicator {
        width: 8px;
        height: 8px;
        top: 2.5px;
        right: 2.5px;
    }
}

/* Safari iOS glass effect fix - minimal approach */
:global(body.modal-open-safari-fix) {
    /* Prevent touch scrolling without changing position */
    touch-action: none !important;
    user-select: none !important;
    /* Keep overflow as auto to maintain backdrop-filter */
    overflow: auto !important;
}

/* Quick Transaction Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    animation: fadeIn 0.2s ease-out;
    overflow: hidden;
    touch-action: none;
}

.modal-content {
    background: var(--light-card);
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideIn 0.3s ease-out;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid var(--light-border);
    margin-bottom: 0;
}

.modal-title {
    color: var(--light-text);
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
}

.btn-close {
    background: none;
    border: none;
    color: var(--light-text-muted);
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
}

.btn-close:hover {
    background: var(--nested-container-bg);
    color: var(--light-text);
}

.modal-body {
    padding: 0;
}

.modal-body :deep(.card) {
    border: none;
    box-shadow: none;
    margin: 0;
    background: transparent;
}

.modal-body :deep(.card-header) {
    display: none;
}

.modal-body :deep(.card-body) {
    padding: 1.5rem;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Mobile modal adjustments */
@media (max-width: 768px) {
    .modal-overlay {
        padding: 0.5rem;
    }

    .modal-content {
        max-height: 95vh;
        border-radius: 8px;
    }

    .modal-header {
        padding: 1rem;
    }

    .modal-title {
        font-size: 1.1rem;
    }

    .modal-body :deep(.card-body) {
        padding: 1rem;
    }
}

/* Theme Picker Modal */
.theme-modal {
    max-width: 500px;
}

.theme-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
    padding: 1.5rem;
}

.theme-option {
    cursor: pointer;
    border-radius: 12px;
    border: 2px solid var(--light-border);
    background: var(--nested-container-bg);
    transition: all 0.2s ease;
    overflow: hidden;
}

.theme-option:hover {
    border-color: var(--light-text);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.theme-option.active {
    border-color: var(--btn-primary-color);
    box-shadow: 0 4px 16px color-mix(in srgb, var(--btn-primary-color) 30%, transparent);
}

.theme-preview {
    position: relative;
    padding: 1rem;
    background: var(--light-card);
}

.preview-colors {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.color-bar {
    height: 32px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.theme-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: 0 auto;
    border-radius: 50%;
    background: var(--nested-container-bg);
    color: var(--light-text);
    font-size: 1.25rem;
}

.theme-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: var(--nested-container-bg);
}

.theme-name {
    font-weight: 600;
    color: var(--light-text);
    font-size: 0.95rem;
}

.theme-active-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--btn-primary-color);
    color: white;
    font-size: 0.75rem;
}

@media (max-width: 768px) {
    .theme-grid {
        grid-template-columns: 1fr;
        padding: 1rem;
    }
}

/* Financial Assistant Modal - Full Screen on Mobile */
.assistant-modal-overlay {
    padding: 0;
}

.assistant-modal-content {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
}

.assistant-close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    background: transparent;
    border: none;
    color: var(--light-text);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.assistant-close-btn:hover {
    background: var(--nested-container-bg);
    transform: scale(1.05);
}

.assistant-close-btn:active {
    transform: scale(0.95);
}

.assistant-close-btn i {
    font-size: 1.5rem;
}

.assistant-modal-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.assistant-modal-body :deep(.card) {
    height: 100%;
    border: none;
    box-shadow: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
}

.assistant-modal-body :deep(.card-header) {
    flex-shrink: 0;
}

.assistant-modal-body :deep(.card-body) {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 0;
}

.assistant-modal-body :deep(.chat-messages) {
    flex: 1;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

.assistant-modal-body :deep(.chat-input-container) {
    flex-shrink: 0;
}
</style>